import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import FavStarFillIcon from '@wix/da-ds/pkg/Icons/24x24/FavStarFill';
import { DefaultBiEvent, BiData, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import useDeviationStats from '../useDeviationStats';
import Stat from '../Stat';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';

export interface Props {
  deviation: PapiDeviation;
  className?: string;
  /** don't display language after stat count */
  onlyNumbers?: boolean;
  /** should display tooltip list of users that have favourited */
  withUserList?: boolean;
  showFavouritedByModal: (d: PapiDeviation) => void;
}

const FaveStat: React.FC<Props> = ({
  deviation,
  onlyNumbers,
  className,
  withUserList,
  showFavouritedByModal,
}) => {
  const { t } = useTranslation();
  const { faves, isFavouritable } = useDeviationStats(deviation);

  const openFavouritedModal = useCallback(() => {
    yieldToMain(() => {
      faves > 0 && showFavouritedByModal(deviation);
    });
  }, [faves, showFavouritedByModal, deviation]);

  return !isFavouritable ? null : (
    <Stat
      className={className}
      deviation={deviation}
      icon={FavStarFillIcon}
      statNameLocaleKey="deviation.stats.faves"
      statCount={faves}
      onlyNumbers={onlyNumbers}
      userListType={withUserList ? 'favourite' : undefined}
      onClick={openFavouritedModal}
      title={t('deviation.stats.tooltip.faves')}
      biData={BiData<DefaultBiEvent>({
        evid: 2,
        click_info: 'favourites',
        sectionname: BiContextProvidedValue,
        widgetname: BiContextProvidedValue,
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
      })}
    />
  );
};
FaveStat.displayName = 'FaveStat';

export default FaveStat;
